import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  TriaxialCrossTest,
  TriaxialGeneral,
  TriaxialStage,
} from '../../../types/triaxial';
import {
  SAMPLE_PREP_OPTIONS,
  TRIMMING_OPTIONS,
  SIDE_DRAINS_TYPE_OPTIONS,
  AREA_METHOD_OPTIONS,
  FAILURE_CRITERION_OPTIONS,
} from '../config/triaxialFields';
import { TriaxialSummaryTable } from './TriaxialSummaryTable';
import { TriaxialAutoPullPanel } from './TriaxialAutoPullPanel';
import {
  computeInitialProperties,
  computeFinalProperties,
  computeFinalDryMassG,
  reconstructPostConsVolumeMm3,
} from './computations';
// BUG-2026-0060 — `<TextField type="number">` + `String(Number(v))` strips
// trailing zeros while typing (e.g. "0.0" round-trips to "0", wiping the
// second zero of "0.05"). `NumericTextField` keeps a local draft string and
// only commits a parsed number to the parent, so decimal entry behaves
// naturally. Same wrapper PSD adopted via BUG-2026-0029.
import { NumericTextField } from '../../../components/forms/NumericTextField';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

interface Props {
  general: TriaxialGeneral;
  stages: TriaxialStage[];
  crossTest: TriaxialCrossTest | null;
  onChange: (field: keyof TriaxialGeneral, value: string | number | boolean | null) => void;
  onStagesChange: (next: TriaxialStage[]) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

// ── Shared styling ──────────────────────────────────────────────────────────
// BUG-2026-0061 — row spacing was too tight: an outlined `<TextField>` label
// in its shrunk state sits ~6–7px above the input's top border, so a 4px
// `gap` between rows caused the row-N+1 label to bleed into row-N's input
// box. Bumping vertical padding and using a larger `rowGap` on the 2-column
// grids below leaves clearance for the floating label without changing the
// horizontal density.
const inputSx = {
  '& .MuiInputBase-input': { py: 0.6, px: 1, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.84rem' },
  '& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
};

// 2-column field grid: row gap large enough to clear the shrunk label of
// the row below; column gap stays tight so paper sections don't grow wide.
const fieldGridSx = { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 0.5, rowGap: 1.25 } as const;

// BUG-2026-0131 — shared column template for the two section grids (top
// properties row + stage-specific row). Using ONE template means the three
// section columns line up vertically top-to-bottom across both rows instead
// of drifting (the two grids previously used different fr ratios).
const SECTION_GRID_COLUMNS = '1.2fr 1.2fr 1.4fr';

const unit = (s: string) => (
  <InputAdornment position="end">
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>{s}</Typography>
  </InputAdornment>
);

const SectionHeader = ({ label, color }: { label: string; color?: string }) => (
  <Typography variant="overline" sx={{
    fontWeight: 700, color: color ?? 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6,
  }}>
    {label}
  </Typography>
);

export const TriaxialParametersTab = ({
  general, stages, crossTest, onChange, onStagesChange, readOnly, workOrderDetailKey,
}: Props) => {
  const theme = useTheme();

  // `toNum` is only used by the shear-strain-rate handler (which converts
  // %/hr → mm/min) — every other numeric input now goes through
  // NumericTextField, which manages its own parsing.
  const toNum = (s: string): number | null => (s === '' ? null : (Number.isFinite(Number(s)) ? Number(s) : null));

  // ── Auto-pull Gs from PSD when the operator hasn't entered one ──────────
  // One-shot: only fires when general's Gs is null *and* the cross-test pull
  // returned a value. Subsequent edits to the field keep the operator's value.
  useEffect(() => {
    if (general.specificGravityGs == null && crossTest?.specificGravityGs != null) {
      onChange('specificGravityGs', crossTest.specificGravityGs);
      if (general.gsAssumedOrMeasured == null) {
        onChange('gsAssumedOrMeasured', 'Assumed');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossTest?.specificGravityGs]);

  // ── Live preview of Initial Properties via the frontend mirror ──────────
  // Recomputes on every keystroke so bulk density, void ratio, S_r update
  // before the operator hits Save. `finalDryMassG` (derived from the pan
  // weighing, cheap helper — no volume needed) drives initial water content
  // + dry density per BUG-2026-0053.
  const liveFinalDryMassG = useMemo(
    () => computeFinalDryMassG(general.massOfTinG, general.finalDryMassPlusTin1G),
    [general.massOfTinG, general.finalDryMassPlusTin1G],
  );

  const liveInitial = useMemo(() => computeInitialProperties({
    diameters: [general.diameter1, general.diameter2, general.diameter3,
      general.diameter4, general.diameter5, general.diameter6],
    lengths: [general.length1, general.length2, general.length3,
      general.length4, general.length5, general.length6],
    perimeterMm: general.perimeterMm,
    massOfWetSampleG: general.massOfWetSampleG,
    massOfPanG: general.massOfPanG,
    massWetSamplePlusPanG: general.massWetSamplePlusPanG,
    massDrySamplePlusPanG: general.massDrySamplePlusPanG,
    massExtraG: general.massExtraG,
    specificGravityGs: general.specificGravityGs ?? crossTest?.specificGravityGs ?? null,
    initialVolumeOverrideMm3: general.initialVolumeMm3,
    finalDryMassG: liveFinalDryMassG,
  }), [
    general.diameter1, general.diameter2, general.diameter3,
    general.diameter4, general.diameter5, general.diameter6,
    general.length1, general.length2, general.length3,
    general.length4, general.length5, general.length6,
    general.perimeterMm, general.massOfWetSampleG, general.massOfPanG,
    general.massWetSamplePlusPanG, general.massDrySamplePlusPanG, general.massExtraG,
    general.specificGravityGs, general.initialVolumeMm3,
    crossTest?.specificGravityGs, liveFinalDryMassG,
  ]);

  // Convenience: prefer the live value (current keystrokes) over the
  // persisted Computed* (which only updates after Save).
  const showAvgDiameter = liveInitial.avgDiameterMm ?? general.computedAvgDiameterMm;
  const showAvgLength   = liveInitial.avgLengthMm ?? general.computedAvgLengthMm;
  const showCalcDiameter = liveInitial.calcDiameterMm ?? general.computedCalcDiameterMm;
  const showInitialWetMass = liveInitial.initialWetMassG;
  const showBulkDensity = liveInitial.bulkDensityMgPerM3 ?? general.computedInitialBulkDensityMgPerM3;
  const showDryDensity  = liveInitial.dryDensityMgPerM3 ?? general.computedInitialDryDensityMgPerM3;
  const showWaterContent = liveInitial.waterContentPct ?? general.computedInitialWaterContentPct;

  // ── Live preview of Final Properties (pan-based, BUG-2026-0054) ─────────
  // Volume basis (BUG-2026-0107): the backend computes Final Bulk/Dry Density,
  // Void Ratio and Sr against the POST-CONSOLIDATION volume (initial volume is
  // only a fallback). This preview used to hardcode postConsVolumeMm3: null,
  // so once consolidation data existed the panel showed initial-volume
  // densities (e.g. 1.33) while the SAMPLE PROPERTIES table / PDF showed the
  // post-cons-based values (1.26). Reconstruct V_pc from the persisted
  // reported geometry (D_pc, H_pc — method-consistent per BUG-2026-0108, NOT
  // the Method-A area, which diverges from the density basis on Method-B
  // tests) so both surfaces agree; stays null pre-consolidation → same
  // initial-volume fallback as the backend.
  const persistedPostConsVolumeMm3 = useMemo(
    () => reconstructPostConsVolumeMm3(general.computedPostConsDiameterCm, general.computedPostConsHeightCm),
    [general.computedPostConsDiameterCm, general.computedPostConsHeightCm],
  );

  const liveFinal = useMemo(() => computeFinalProperties({
    massOfTinG: general.massOfTinG,
    finalWetMassPlusTinG: general.finalWetMassPlusTinG,
    finalDryMassPlusTin1G: general.finalDryMassPlusTin1G,
    specificGravityGs: general.specificGravityGs ?? crossTest?.specificGravityGs ?? null,
    postConsVolumeMm3: persistedPostConsVolumeMm3,
    initialVolumeMm3: liveInitial.initialVolumeMm3,
  }), [
    general.massOfTinG, general.finalWetMassPlusTinG,
    general.finalDryMassPlusTin1G,
    general.specificGravityGs, crossTest?.specificGravityGs,
    persistedPostConsVolumeMm3,
    liveInitial.initialVolumeMm3,
  ]);

  const showFinalDryMass    = liveFinal.finalDryMassG;
  const showFinalWaterContent = liveFinal.waterContentPct;
  const showFinalBulkDensity  = liveFinal.bulkDensityMgPerM3 ?? general.computedFinalBulkDensityMgPerM3;
  const showFinalDryDensity   = liveFinal.dryDensityMgPerM3 ?? general.computedFinalDryDensityMgPerM3;
  const showFinalVoidRatio    = liveFinal.voidRatio ?? general.computedFinalVoidRatio;
  const showFinalDegreeOfSat  = liveFinal.degreeOfSatPct ?? general.computedFinalDegreeOfSatPct;

  // ── Live preview: Deformation during Saturation = End − Initial ─────────
  // Mirrors backend cache calc in triaxialService.recomputeAndPersist so the
  // operator sees the value update as they type, not only after Save.
  const liveDeformationDuringSat = useMemo(() => {
    const init = general.initialDeformationMm;
    const end = general.endOfSaturationDeformationMm;
    if (init == null && end == null) return null;
    return (end ?? 0) - (init ?? 0);
  }, [general.initialDeformationMm, general.endOfSaturationDeformationMm]);
  const showDeformationDuringSat =
    liveDeformationDuringSat ?? general.computedDeformationDuringSaturationMm;

  // `setNumVal` is the NumericTextField-shaped equivalent of the old
  // `setNum`. NumericTextField hands the parent a parsed `number | null`
  // directly (no event), so we just forward it to `onChange`.
  const setNumVal = (field: keyof TriaxialGeneral) => (v: number | null) =>
    onChange(field, v);
  const setStr = (field: keyof TriaxialGeneral) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange(field, e.target.value === '' ? null : e.target.value);
  const setBool = (field: keyof TriaxialGeneral) => (_: any, checked: boolean) =>
    onChange(field, checked);

  // ── Shear strain rate (%/hr): user-entered input, mm/min derived ──
  // BUG-2026-0056: lab convention is to specify shear strain rate in %/hr;
  // mm/min (what the machine consumes) is computed from a specimen height.
  // Canonical storage stays in `shearRateMmPerMin` so existing tests render
  // correctly via the bidirectional conversion.
  //
  // BUG-2026-0102: the strain-rate input used to be locked until
  // `computedPostConsHeightCm` existed. But that value is server-computed and
  // only appears after the operator has entered specimen properties + ≥2
  // consolidation rows AND saved — which is *after* the test is set up. Since
  // the strain rate is a setup parameter the operator wants to specify up
  // front, the field was effectively dead during normal entry ("can't put any
  // input"). Fall back to the live initial specimen height (`showAvgLength`)
  // for the %/hr↔mm/min conversion when the post-consolidation height isn't
  // known yet, so the field is usable as soon as the specimen dimensions are
  // entered. On save the backend recomputes the post-consolidation strain rate
  // from the stored mm/min against the true H_pc, and the effect below
  // re-syncs the displayed %/hr.
  const postConsHeightMm = (general.computedPostConsHeightCm ?? 0) * 10;
  // Conversion height: prefer the post-consolidation height; fall back to the
  // live initial average specimen length (mm) so entry isn't blocked pre-save.
  const conversionHeightMm = postConsHeightMm > 0 ? postConsHeightMm : (showAvgLength ?? 0);
  const canEditStrainRate = conversionHeightMm > 0;
  // True once we're using the provisional initial-height fallback rather than
  // the consolidated height — drives a softer "will be refined" helper note.
  const usingProvisionalHeight = canEditStrainRate && postConsHeightMm <= 0;

  const [strainRatePctPerHrInput, setStrainRatePctPerHrInput] = useState<string>('');

  useEffect(() => {
    if (general.shearRateMmPerMin == null || conversionHeightMm <= 0) {
      if (strainRatePctPerHrInput !== '') setStrainRatePctPerHrInput('');
      return;
    }
    const derived = (general.shearRateMmPerMin * 6000) / conversionHeightMm;
    const currentParsed = strainRatePctPerHrInput === '' ? null : Number(strainRatePctPerHrInput);
    if (currentParsed == null || !Number.isFinite(currentParsed) || Math.abs(currentParsed - derived) > 1e-6) {
      // Trim trailing zeros while keeping reasonable precision.
      const formatted = derived.toFixed(4).replace(/\.?0+$/, '');
      setStrainRatePctPerHrInput(formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [general.shearRateMmPerMin, conversionHeightMm]);

  const handleStrainRatePctPerHrChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const str = e.target.value;
    setStrainRatePctPerHrInput(str);
    if (conversionHeightMm <= 0) return;
    const pctPerHr = toNum(str);
    if (pctPerHr == null) {
      onChange('shearRateMmPerMin', null);
    } else {
      const mmPerMin = (pctPerHr * conversionHeightMm) / 6000;
      onChange('shearRateMmPerMin', mmPerMin);
    }
  };

  // ── Consolidation stage rows ──
  const fromStage = general.consolidationFromStage ?? null;
  const toStage = general.consolidationToStage ?? null;
  const stageRows: number[] = [];
  if (fromStage !== null && toStage !== null && toStage >= fromStage) {
    for (let n = fromStage; n <= toStage; n++) stageRows.push(n);
  }
  const stageByNumber = new Map(stages.map((s) => [s.stageNumber, s]));
  const updateStagePressure = (stageNumber: number, val: number | null) => {
    const next: TriaxialStage[] = stageRows.map((n, idx) => {
      const existing = stageByNumber.get(n);
      return {
        triaxialDetailKey: existing?.triaxialDetailKey,
        stageNumber: n,
        cellPressureKPa: n === stageNumber ? val : (existing?.cellPressureKPa ?? null),
        sortOrder: idx,
      };
    });
    onStagesChange(next);
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Header — Test Type indicator + Test ID ───────────────── */}
      <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Test Type:
        </Typography>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
          {general.testType} — {general.testType === 'CIU' ? 'Consolidated Undrained (ASTM D4767)' : 'Consolidated Drained (ASTM D7181)'}
        </Typography>
        {/* BUG-2026-0117 — manual Test ID; prints in the report header
            directly under the ASTM number (e.g. "TEST ID - CIU2519023001"). */}
        <TextField
          label="Test ID" size="small" disabled={readOnly}
          placeholder="e.g. CIU2519023001"
          sx={{ ...inputSx, ml: 'auto', width: 260 }}
          value={general.testId ?? ''} onChange={setStr('testId')}
          helperText="Printed under the ASTM number on the report"
          FormHelperTextProps={{ sx: { fontSize: '0.62rem', mt: 0.25, mx: 0.5 } }}
        />
      </Paper>

      {/* ── Three-column top row ──────────────────────────────────────
          BUG-2026-0131 — explicit 3-col × 2-row grid so (a) the three section
          columns line up vertically with the stage-specific row below, (b) the
          divider between the stacked middle/right cards is one continuous
          horizontal line (both cards sit on the grid's row boundary), and
          (c) every card in a row stretches to equal height (aligned top &
          bottom bands). The left card spans both rows. The middle/right
          wrappers use `display:contents` so their two Papers become direct
          grid items without disturbing the JSX nesting. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: SECTION_GRID_COLUMNS,
        gridTemplateRows: 'auto auto',
        alignItems: 'stretch',
        gap: 2,
      }}>
        {/* ── LEFT: Initial Physical Properties (spans both rows) ── */}
        <Paper sx={{ p: 1.5, gridColumn: '1', gridRow: '1 / 3' }}>
          <SectionHeader label="Initial Physical Properties" />
          <TextField
            label="Station Number" size="small" fullWidth disabled={readOnly}
            sx={{ ...inputSx, mt: 1, mb: 1 }}
            value={general.stationNumber ?? ''} onChange={setStr('stationNumber')}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
            Initial Specimen Dimensions
          </Typography>
          <Box sx={{ ...fieldGridSx, mt: 0.5 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Box key={i} sx={{ display: 'contents' }}>
                <NumericTextField label={`Ø${i} (mm)`} size="small" disabled={readOnly} sx={inputSx}
                  value={general[`diameter${i}` as keyof TriaxialGeneral] as number | null}
                  onChange={setNumVal(`diameter${i}` as keyof TriaxialGeneral)} />
                <NumericTextField label={`L${i} (mm)`} size="small" disabled={readOnly} sx={inputSx}
                  value={general[`length${i}` as keyof TriaxialGeneral] as number | null}
                  onChange={setNumVal(`length${i}` as keyof TriaxialGeneral)} />
              </Box>
            ))}
          </Box>
          <Box sx={{ ...fieldGridSx, mt: 1.5 }}>
            <TextField label="Avg Diameter" size="small" InputProps={{ readOnly: true, endAdornment: unit('mm') }}
              sx={{ ...inputSx, bgcolor: 'action.hover' }}
              value={showAvgDiameter?.toFixed(3) ?? '—'} />
            <TextField label="Avg Length" size="small" InputProps={{ readOnly: true, endAdornment: unit('mm') }}
              sx={{ ...inputSx, bgcolor: 'action.hover' }}
              value={showAvgLength?.toFixed(3) ?? '—'} />
          </Box>
        </Paper>

        {/* ── MIDDLE: Specimen Characteristics + Membrane Information (stacked) ── */}
        <Box sx={{ display: 'contents' }}>
          {/* Specimen Characteristics & Materials (col 2, row 1) */}
          <Paper sx={{ p: 1.5, gridColumn: '2', gridRow: '1' }}>
            <SectionHeader label="Specimen Characteristics & Materials" />
            <TextField
              select label="Sample Prep Method" size="small" fullWidth disabled={readOnly}
              sx={{ ...inputSx, mt: 1 }}
              value={general.samplePrepMethod ?? ''}
              onChange={(e) => onChange('samplePrepMethod', e.target.value === '' ? null : (e.target.value as any))}
            >
              {SAMPLE_PREP_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
            {general.samplePrepMethod === 'ReconstitutedOther' && (
              <TextField
                label="Specify method" size="small" fullWidth disabled={readOnly}
                sx={{ ...inputSx, mt: 1 }}
                value={general.samplePrepOtherText ?? ''} onChange={setStr('samplePrepOtherText')}
              />
            )}
            <TextField
              select label="Trimming Procedure" size="small" fullWidth disabled={readOnly}
              sx={{ ...inputSx, mt: 1 }}
              value={general.trimmingProcedure ?? ''}
              onChange={(e) => onChange('trimmingProcedure', e.target.value === '' ? null : (e.target.value as any))}
            >
              <MenuItem value="" sx={{ fontSize: '0.78rem' }}>—</MenuItem>
              {TRIMMING_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ ...fieldGridSx, mt: 1 }}>
              <NumericTextField label="Specific Gravity Gs" size="small" disabled={readOnly} sx={inputSx}
                placeholder={crossTest?.specificGravityGs ? `from PSD: ${crossTest.specificGravityGs}` : ''}
                value={general.specificGravityGs} onChange={setNumVal('specificGravityGs')} />
              <TextField
                select label="Gs Source" size="small" disabled={readOnly} sx={inputSx}
                value={general.gsAssumedOrMeasured ?? ''}
                onChange={(e) => onChange('gsAssumedOrMeasured', e.target.value === '' ? null : (e.target.value as any))}
              >
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}>—</MenuItem>
                <MenuItem value="Assumed" sx={{ fontSize: '0.78rem' }}>Assumed</MenuItem>
                <MenuItem value="Measured" sx={{ fontSize: '0.78rem' }}>Measured</MenuItem>
              </TextField>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={!!general.sideDrainsUsed} onChange={setBool('sideDrainsUsed')} disabled={readOnly} size="small" />}
              label={<Typography variant="caption">Side Drains Used</Typography>}
              sx={{ mt: 1 }}
            />
            <TextField
              select label="Side Drains Type" size="small" fullWidth
              disabled={readOnly || !general.sideDrainsUsed}
              sx={{ ...inputSx, mt: 0.5 }}
              value={general.sideDrainsType ?? ''}
              onChange={(e) => onChange('sideDrainsType', e.target.value === '' ? null : (e.target.value as any))}
            >
              <MenuItem value="" sx={{ fontSize: '0.78rem' }}>—</MenuItem>
              {SIDE_DRAINS_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Paper>

          {/* Membrane Information — split out per BUG-2026-0051 (col 2, row 2) */}
          <Paper sx={{ p: 1.5, gridColumn: '2', gridRow: '2' }}>
            <SectionHeader label="Membrane Information" />
            <Box sx={{ ...fieldGridSx, mt: 1 }}>
              <NumericTextField label="Membrane Stiffness" size="small" InputProps={{ endAdornment: unit('kPa') }}
                disabled={readOnly} sx={inputSx}
                value={general.membraneStiffnessKPa} onChange={setNumVal('membraneStiffnessKPa')} />
              <NumericTextField label="Wall Thickness" size="small" InputProps={{ endAdornment: unit('mm') }}
                disabled={readOnly} sx={inputSx}
                value={general.wallThicknessMm} onChange={setNumVal('wallThicknessMm')} />
            </Box>
            <NumericTextField label="Perimeter" required size="small" InputProps={{ endAdornment: unit('mm') }}
              disabled={readOnly} sx={{ ...inputSx, mt: 1 }} fullWidth
              value={general.perimeterMm} onChange={setNumVal('perimeterMm')} />
            <TextField
              label="Calculated Diameter (from perimeter)" size="small" InputProps={{ readOnly: true, endAdornment: unit('mm') }}
              sx={{ ...inputSx, bgcolor: 'action.hover', mt: 1 }} fullWidth
              value={showCalcDiameter?.toFixed(3) ?? '—'}
            />
          </Paper>
        </Box>

        {/* ── RIGHT: Initial + Final Properties (stacked) ────────── */}
        <Box sx={{ display: 'contents' }}>
          {/* Initial Properties (Mass & Density) — col 3, row 1
              BUG-2026-0053: Initial Wet Mass is computed (Wet+Former − Former),
              Initial Dry Mass input removed. Water content + dry density
              back-calc from the pan-based Final Dry Mass below. */}
          <Paper sx={{ p: 1.5, gridColumn: '3', gridRow: '1' }}>
            <SectionHeader label="Initial Properties (Mass & Density)" />
            <Box sx={{ ...fieldGridSx, mt: 1 }}>
              <NumericTextField label="Mass of Former" size="small" InputProps={{ endAdornment: unit('g') }}
                disabled={readOnly} sx={inputSx} placeholder="0"
                value={general.massOfPanG} onChange={setNumVal('massOfPanG')} />
              <NumericTextField label="Initial Wet Mass + Former" size="small" InputProps={{ endAdornment: unit('g') }}
                disabled={readOnly} sx={inputSx}
                value={general.massWetSamplePlusPanG} onChange={setNumVal('massWetSamplePlusPanG')} />
              <TextField label="Initial Wet Mass" size="small" InputProps={{ readOnly: true, endAdornment: unit('g') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showInitialWetMass?.toFixed(2) ?? '—'} />
              <TextField label="Initial Water Content" size="small" InputProps={{ readOnly: true, endAdornment: unit('%') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showWaterContent?.toFixed(1) ?? '—'} />
              <TextField label="Initial Bulk Density" size="small" InputProps={{ readOnly: true, endAdornment: unit('Mg/m³') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showBulkDensity?.toFixed(2) ?? '—'} />
              <TextField label="Initial Dry Density" size="small" InputProps={{ readOnly: true, endAdornment: unit('Mg/m³') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showDryDensity?.toFixed(2) ?? '—'} />
            </Box>
          </Paper>

          {/* Final Properties (Mass & Density) — pan-based inputs (col 3, row 2) */}
          <Paper sx={{ p: 1.5, gridColumn: '3', gridRow: '2' }}>
            <SectionHeader label="Final Properties (Mass & Density)" />
            <Box sx={{ ...fieldGridSx, mt: 1 }}>
              <NumericTextField label="Mass of Pan" size="small" InputProps={{ endAdornment: unit('g') }}
                disabled={readOnly} sx={inputSx} placeholder="0"
                value={general.massOfTinG} onChange={setNumVal('massOfTinG')} />
              <NumericTextField label="Final Wet Mass + Pan" size="small" InputProps={{ endAdornment: unit('g') }}
                disabled={readOnly} sx={inputSx}
                value={general.finalWetMassPlusTinG} onChange={setNumVal('finalWetMassPlusTinG')} />
              <NumericTextField label="Final Dry Mass + Pan" size="small" InputProps={{ endAdornment: unit('g') }}
                disabled={readOnly} sx={inputSx}
                value={general.finalDryMassPlusTin1G} onChange={setNumVal('finalDryMassPlusTin1G')} />
              <TextField label="Final Dry Mass" size="small" InputProps={{ readOnly: true, endAdornment: unit('g') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalDryMass?.toFixed(2) ?? '—'} />
              <TextField label="Final Water Content" size="small" InputProps={{ readOnly: true, endAdornment: unit('%') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalWaterContent?.toFixed(1) ?? '—'} />
              <TextField label="Final Bulk Density" size="small" InputProps={{ readOnly: true, endAdornment: unit('Mg/m³') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalBulkDensity?.toFixed(2) ?? '—'} />
              <TextField label="Final Dry Density" size="small" InputProps={{ readOnly: true, endAdornment: unit('Mg/m³') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalDryDensity?.toFixed(2) ?? '—'} />
              <TextField label="Final Void Ratio" size="small" InputProps={{ readOnly: true }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalVoidRatio?.toFixed(2) ?? '—'} />
              <TextField label="Final Sr" size="small" InputProps={{ readOnly: true, endAdornment: unit('%') }}
                sx={{ ...inputSx, bgcolor: 'action.hover' }}
                value={showFinalDegreeOfSat?.toFixed(0) ?? '—'} />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Stage-Specific Parameters ───────────────────────────────
          BUG-2026-0131 — same SECTION_GRID_COLUMNS template + stretch as the
          top row so the three columns line up with the properties row above
          and the cards share a common bottom edge. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: SECTION_GRID_COLUMNS,
        alignItems: 'stretch',
        gap: 2,
      }}>
        {/* SATURATION */}
        <Paper sx={{ p: 1.5 }}>
          <SectionHeader label="Saturation" color={theme.palette.success.dark} />
          <NumericTextField label="Initial Deformation" size="small" InputProps={{ endAdornment: unit('mm') }}
            disabled={readOnly} sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.initialDeformationMm} onChange={setNumVal('initialDeformationMm')} />
          <NumericTextField label="End of Saturation Deformation" size="small" InputProps={{ endAdornment: unit('mm') }}
            disabled={readOnly} sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.endOfSaturationDeformationMm} onChange={setNumVal('endOfSaturationDeformationMm')} />
          <TextField label="Deformation during Saturation"
            size="small" InputProps={{ readOnly: true, endAdornment: unit('mm') }}
            sx={{ ...inputSx, bgcolor: 'action.hover', mt: 1 }} fullWidth
            value={showDeformationDuringSat?.toFixed(3) ?? '—'} />
          <NumericTextField label="B-Value" size="small"
            disabled={readOnly} sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.bValue} onChange={setNumVal('bValue')} />
        </Paper>

        {/* CONSOLIDATION */}
        <Paper sx={{ p: 1.5 }}>
          <SectionHeader label="Consolidation" color={theme.palette.info.dark} />
          <Box sx={{ ...fieldGridSx, mt: 1 }}>
            <NumericTextField label="From Stage" size="small" disabled={readOnly} sx={inputSx}
              value={general.consolidationFromStage} onChange={setNumVal('consolidationFromStage')} />
            <NumericTextField label="To Stage" size="small" disabled={readOnly} sx={inputSx}
              value={general.consolidationToStage} onChange={setNumVal('consolidationToStage')} />
          </Box>
          <NumericTextField label="Back Pressure Applied" size="small"
            InputProps={{ endAdornment: unit('kPa') }} disabled={readOnly}
            sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.backPressureAppliedKPa} onChange={setNumVal('backPressureAppliedKPa')} />
          <NumericTextField label="Average PWP during Consolidation" size="small"
            InputProps={{ endAdornment: unit('kPa') }} disabled={readOnly}
            sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.averagePwpDuringConsolidationKPa} onChange={setNumVal('averagePwpDuringConsolidationKPa')} />
          <TextField select label="Area Calculation Method" size="small" disabled={readOnly}
            sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.areaCalculationMethod ?? ''}
            onChange={(e) => onChange('areaCalculationMethod', e.target.value === '' ? null : (e.target.value as any))}
          >
            {AREA_METHOD_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
            ))}
          </TextField>
          {/* Stage cell-pressure rows */}
          {stageRows.length > 0 && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Cell Pressures per Stage
              </Typography>
              {stageRows.map((n) => (
                <NumericTextField key={n} label={`Stage ${n} (kPa)`} size="small" disabled={readOnly}
                  sx={{ ...inputSx, mt: 0.5 }} fullWidth
                  value={stageByNumber.get(n)?.cellPressureKPa ?? null}
                  onChange={(v) => updateStagePressure(n, v)} />
              ))}
            </Box>
          )}
        </Paper>

        {/* SHEAR */}
        <Paper sx={{ p: 1.5 }}>
          <SectionHeader label={`Shear (${general.testType})`} color={theme.palette.warning.dark} />
          <Box sx={{ ...fieldGridSx, mt: 1 }}>
            <NumericTextField label="Shear Stage #" size="small" disabled={readOnly} sx={inputSx}
              value={general.shearStageNumber} onChange={setNumVal('shearStageNumber')} />
            <NumericTextField label="Calibrated Cell Pressure" size="small"
              InputProps={{ endAdornment: unit('kPa') }} disabled={readOnly} sx={inputSx}
              value={general.calibratedCellPressureKPa} onChange={setNumVal('calibratedCellPressureKPa')} />
          </Box>
          {/* BUG-2026-0056: %/hr is the user input; mm/min is derived from H_pc */}
          <Box sx={{ ...fieldGridSx, mt: 1 }}>
            <TextField
              label="Shear Strain Rate"
              size="small"
              // BUG-2026-0060 — type="text" + inputMode="decimal" so the
              // browser doesn't sanitize intermediate states like "0.0" to
              // "0" mid-typing. Local string state (`strainRatePctPerHrInput`)
              // already prevents the parent round-trip from clobbering the
              // draft, but the input element itself still needed the swap.
              type="text"
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: unit('%/hr') }}
              disabled={readOnly || !canEditStrainRate}
              helperText={
                !canEditStrainRate
                  ? 'Enter specimen dimensions first'
                  : usingProvisionalHeight
                    ? 'Using initial height; rate refines after consolidation'
                    : undefined
              }
              FormHelperTextProps={{ sx: { fontSize: '0.6rem', mt: 0.25, mx: 0 } }}
              sx={inputSx}
              value={strainRatePctPerHrInput}
              onChange={handleStrainRatePctPerHrChange}
            />
            <TextField
              label="Shear Rate"
              size="small"
              InputProps={{ readOnly: true, endAdornment: unit('mm/min') }}
              sx={{ ...inputSx, bgcolor: 'action.hover' }}
              value={general.shearRateMmPerMin == null ? '—' : general.shearRateMmPerMin.toFixed(4)}
            />
          </Box>
          <TextField select label="Failure Criterion" size="small" disabled={readOnly}
            sx={{ ...inputSx, mt: 1 }} fullWidth
            value={general.failureCriterion ?? ''}
            onChange={(e) => onChange('failureCriterion', e.target.value === '' ? null : (e.target.value as any))}
          >
            {FAILURE_CRITERION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
            ))}
          </TextField>
          {general.failureCriterion === 'Other' && (
            <TextField label="Specify failure criterion" size="small" fullWidth disabled={readOnly}
              sx={{ ...inputSx, mt: 1 }}
              value={general.failureCriterionOtherText ?? ''} onChange={setStr('failureCriterionOtherText')} />
          )}
        </Paper>
      </Box>

      {/* ── Correction Toggles ────────────────────────────────────── */}
      <Paper sx={{ p: 1.5, display: 'flex', gap: 3, alignItems: 'center' }}>
        <SectionHeader label="Corrections (ASTM D4767 §13)" />
        <FormControlLabel
          control={<Checkbox checked={!!general.applyMembraneCorrection} onChange={setBool('applyMembraneCorrection')} disabled={readOnly} size="small" />}
          label={<Typography variant="caption">Membrane Correction</Typography>}
        />
        <FormControlLabel
          control={<Checkbox checked={!!general.applyFilterPaperCorrection} onChange={setBool('applyFilterPaperCorrection')} disabled={readOnly} size="small" />}
          label={<Typography variant="caption">Filter-Paper Correction</Typography>}
        />
        <FormControlLabel
          control={<Checkbox checked={!!general.applySideDrainCorrection} onChange={setBool('applySideDrainCorrection')} disabled={readOnly} size="small" />}
          label={<Typography variant="caption">Side-Drain Correction</Typography>}
        />
      </Paper>

      {/* ── Auto-pulled from same Sample (REQ-2026-0002 / REQ-08) ──
          Always rendered, even when no upstream Approved test exists.
          Each cell supports an inline override + Reset; overrides are
          audit-logged through AuditResetLog on save. */}
      <TriaxialAutoPullPanel
        general={general}
        crossTest={crossTest}
        onChange={(field, value) => onChange(field, value)}
        readOnly={readOnly}
      />

      {/* ── Live Summary Table preview ────────────────────────────── */}
      <TriaxialSummaryTable general={general} />

      {/* ── Additional Observations — lab-internal, never printed ── */}
      {workOrderDetailKey && (
        <Paper sx={{ p: 1.5 }}>
          <SectionHeader label="Additional Observations (internal — not printed on the report)" />
          <TextField
            size="small" fullWidth multiline minRows={3}
            placeholder="Internal observations about specimen, test conditions, or equipment — for the lab and reviewer only."
            value={general.comments ?? ''}
            onChange={(e) => onChange('comments', e.target.value || null)}
            InputProps={{ readOnly }} sx={{ ...inputSx, mt: 0.75 }}
          />
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        </Paper>
      )}
    </Box>
  );
};
